import { apiUrl } from '@algoan/dashboard/env';
import { Dispatch, AsyncThunk } from '@algoan/dashboard/store/store';
import {
  fetchAccountsFailure,
  fetchAccountsStart,
  fetchAccountsSuccess,
  CheckedAccount,
  refreshFinish,
  refreshStart,
} from '@algoan/dashboard/store/slice';
import { buildUrl } from '@algoan/dashboard/utils';
import { authRequest } from './auth-thunks';
import {
  Account,
  DailyBalance,
  PaginatedResources,
  AnalysisErrorCodes,
  AnalysisParameters,
  AccountItem,
  NotificationVariant,
} from '@algoan/dashboard/model';
import { analysisPageCancellationToken, fetchNewAggregationData } from './dashboard-thunks';
import { notify } from './app-state-thunks';
import { accountSelectionForAnalysisMessages } from '@algoan/dashboard/store/messages';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { CancelTokenSource } from 'axios';

const fetchDailyBalances =
  (customerId: string, analysisId: string, accountId: string) =>
  async (dispatch: Dispatch): Promise<DailyBalance[]> => {
    const url = buildUrl(`${apiUrl}/v2/customers/${customerId}/analyses/${analysisId}/accounts/${accountId}/balances`, [
      { key: 'page', value: 1 },
      { key: 'limit', value: 500 },
    ]);

    return await dispatch(recursiveFetchDailyBalances(url, []));
  };

const recursiveFetchDailyBalances =
  (nextUrl: string, dailyBalances: DailyBalance[]) =>
  async (dispatch: Dispatch): Promise<DailyBalance[]> => {
    const { data: response } = await dispatch(authRequest<PaginatedResources<DailyBalance>>('GET', nextUrl));
    const { pagination, resources } = response;

    const accumulatedDailyBalances = [...dailyBalances, ...resources];

    return pagination.next
      ? dispatch(recursiveFetchDailyBalances(`${apiUrl}${pagination.next}`, accumulatedDailyBalances))
      : accumulatedDailyBalances;
  };

export const fetchAccounts =
  ({
    customerId,
    analysisId,
    shouldFetchBalances = true,
    activityCancelToken,
  }: {
    customerId: string;
    analysisId: string;
    shouldFetchBalances?: boolean;
    activityCancelToken?: CancelTokenSource;
  }) =>
  async (dispatch: Dispatch): Promise<Account[] | undefined> => {
    try {
      const url = buildUrl(`${apiUrl}/v2/customers/${customerId}/analyses/${analysisId}/accounts`, [
        { key: 'page', value: 1 },
        { key: 'limit', value: 100 },
        { key: 'includeNotComputed', value: 'true' },
      ]);

      dispatch(fetchAccountsStart());
      const accounts = await dispatch(recursiveFetchAccounts(url, [], activityCancelToken));
      if (accounts.length === 0) {
        dispatch(fetchAccountsFailure({ error: 'noAccountsError' }));
        return;
      }

      if (!shouldFetchBalances) {
        dispatch(fetchAccountsSuccess(accounts.map((account) => ({ ...account, dailyBalances: [] }))));
        return accounts;
      }

      const accountsWithDailyBalances = await Promise.all(
        accounts.map(async (account) => {
          const dailyBalances = await dispatch(fetchDailyBalances(customerId, analysisId, account.id));

          return { ...account, dailyBalances };
        }),
      );

      dispatch(fetchAccountsSuccess(accountsWithDailyBalances));
      return accountsWithDailyBalances;
    } catch (error) {
      dispatch(fetchAccountsFailure({ error: 'genericError' }));
    }
    return undefined;
  };

const recursiveFetchAccounts =
  (nextUrl: string, accounts: Account[], activityCancelToken: CancelTokenSource | undefined) =>
  async (dispatch: Dispatch): Promise<Account[]> => {
    const { data: response } = await dispatch(
      authRequest<PaginatedResources<Account>>('GET', nextUrl, undefined, {
        cancelToken: activityCancelToken ? activityCancelToken.token : analysisPageCancellationToken?.token,
      }),
    );
    const { pagination, resources } = response;

    const accumulatedAccounts = [...accounts, ...resources];
    return pagination.next
      ? dispatch(recursiveFetchAccounts(`${apiUrl}${pagination.next}`, accumulatedAccounts, activityCancelToken))
      : accumulatedAccounts;
  };

export const updateAccountsSelectionForAnalysis =
  (
    customerId: string,
    accounts: AccountItem[],
    accountsSelected: CheckedAccount[],
    analysisParameters: AnalysisParameters,
  ): AsyncThunk<void> =>
  async (dispatch) => {
    try {
      dispatch(refreshStart());
      const selectedAccountIds = accountsSelected.map((account) => account.id);
      const accountsItemSelected = accounts.map((account) => ({
        ...account,
        transactions: [],
        isComputed: selectedAccountIds.includes(account.id),
      }));

      const accountSelectionForAnalysisUrl = `${apiUrl}/v2/customers/${customerId}/analyses`;

      const { data } = await dispatch(
        authRequest<{ id: string }>('post', accountSelectionForAnalysisUrl, {
          accounts: accountsItemSelected,
          parameters: analysisParameters,
        }),
      );

      await dispatch(fetchNewAggregationData(customerId, data.id));
      logAmplitudeEvent(AmplitudeEvent.ACCOUNT_SELECTION_COMPLETED);
      dispatch(
        notify({
          type: NotificationVariant.SUCCESS,
          message: accountSelectionForAnalysisMessages.notificationSuccess,
        }),
      );
    } catch (error) {
      const errorMessage = error as AnalysisErrorCodes;
      logAmplitudeEvent(AmplitudeEvent.ACCOUNT_SELECTION_ERROR);
      dispatch(
        notify({
          type: NotificationVariant.ERROR,
          message:
            errorMessage === AnalysisErrorCodes.NO_CHECKING_ACCOUNT
              ? accountSelectionForAnalysisMessages.notificationCheckingError
              : accountSelectionForAnalysisMessages.notificationError,
        }),
      );
    } finally {
      dispatch(refreshFinish());
    }
  };
